import Loading from "@components/Loading";
import CheckoutScreen from "@pages/Checkout";
import CompanyScreen from "@pages/Company";
import EmepagRedirect from "@pages/EmepagRedirect";
import ForgotPasswordScreen from "@pages/ForgotPassword";
import LoginScreen from "@pages/Login";
import LogoutScreen from "@pages/Logout";
import NotFoundPage from "@pages/NotFound";
import OrderScreen from "@pages/Order";
import ProductScreen from "@pages/Product";
import RegisterScreen from "@pages/Register";
import ResetPasswordScreen from "@pages/ResetPassword";
import { RootState, useAppDispatch } from "@services/Redux";
import { fetchUserData, setCompanyCode } from "@services/Redux/app";
import { fetchCompany, fetchCompanyMaintenanceMode, fetchSeller } from "@services/Redux/company";
import { fetchOrder } from "@services/Redux/order";
import { fetchProducts } from "@services/Redux/products";
import { enableSellerModeCommunication, setupSellerData } from "@utils/functions";
import { useWindowSize } from "@utils/hooks";
import LogRocket from "logrocket";
import setupLogRocketReact from "logrocket-react";
import * as React from "react";
import { useSelector } from "react-redux";
import { Route, Routes, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import WhatsappButton from "@components/WhatsappButton";
import MaintenancePage from "@pages/Maintenance";
import TagManager from "react-gtm-module";
import ReactGA from "react-ga4";

// Importing styles
import "@styles/spinner.css";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useCookies } from "react-cookie";
import "react-responsive-modal/styles.css";
import "react-toastify/dist/ReactToastify.min.css";
import "./styles/global.scss";
import env from "@utils/env";
import MetaPixel from "@utils/meta/MetaPixel";

const AppRoutes: React.FC = () => {
  let [searchParams] = useSearchParams();
  const company = useSelector((state: RootState) => state.company);
  const companyCode = useSelector((state: RootState) => state.app.companyCode);
  const loadingUser = useSelector((state: RootState) => state.app.loadingUser);
  const dispatch = useAppDispatch();
  const [, setCookie] = useCookies([`company_${company.data.code}_seller_id`]);
  const { width } = useWindowSize();
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    if (companyCode) dispatch(fetchCompanyMaintenanceMode(companyCode));
    // eslint-disable-next-line
  }, [location]);

  React.useEffect(() => {
    if (company.data.is_maintenance_mode_enabled && location.pathname !== "/manutencao") {
      navigate("/manutencao");
    }
    // eslint-disable-next-line
  }, [company.data.is_maintenance_mode_enabled]);

  React.useEffect(() => {
    if (company?.data?.code) {
      if (company.data.code === "acqualara") {
        console.log(`Starting logrocket`);
        LogRocket.init("87qv2x/catalogo");
        setupLogRocketReact(LogRocket);
      }

      if (company.data.is_maintenance_mode_enabled) {
        navigate("/manutencao");
      }

      // Setting up GTM and analytics
      if (company.data.gtm_container_id) {
        console.log(`Starting GTM ${company.data.gtm_container_id}`);
        TagManager.initialize({ gtmId: company.data.gtm_container_id });
        ReactGA.initialize([
          { trackingId: company.data.gtm_container_id, gaOptions: { name: "gtm" } },
          {
            trackingId: env.REACT_APP_ANALYTICS_ID,
          },
        ]);
      } else {
        ReactGA.initialize(env.REACT_APP_ANALYTICS_ID);
      }

      if (company?.data?.meta_pixel) {
        MetaPixel.init(company?.data?.meta_pixel);
      }

      const sellerId = searchParams.get("vendedor") ?? localStorage.getItem(`company_${company.data.code}_seller_id`) ?? company.data.default_seller?.id;
      if (sellerId) {
        setCookie(`company_${company.data.code}_seller_id`, sellerId, { path: "/" });
        setupSellerData(company.data.code, sellerId);
        dispatch(fetchSeller(`${sellerId}`));
      }
      const sellerMode = searchParams.get("seller_mode") ?? localStorage.getItem(`seller_mode`); // Seller mode is used by the seller to edit an order
      if (sellerMode) {
        enableSellerModeCommunication();
        const orderCode = searchParams.get("order_code") ?? "";
        localStorage.setItem(`company_${company.data.code}_order_code`, orderCode);
        localStorage.setItem(`seller_mode`, sellerMode);
      }

      dispatch(fetchOrder());
      dispatch(fetchUserData());
    }

    // Setting favicon
    if (env.REACT_APP_ICON_URL) {
      const link = document.getElementById("favicon") as any;
      link.href = env.REACT_APP_ICON_URL;
    }
    // eslint-disable-next-line
  }, [dispatch, company.data]);

  React.useEffect(() => {
    if (dispatch && companyCode) {
      dispatch(fetchCompany(companyCode));
    }
    // eslint-disable-next-line
  }, [dispatch, companyCode]);

  React.useEffect(() => {
    const marca = searchParams.get("marca");
    const subdomains = window.location.hostname.split(".");
    console.log(subdomains, "subdomains");
    const subdomain = subdomains.shift() ?? "acqualara";
    console.log(subdomain, "subdomain");
    console.log(env.REACT_APP_DOMAIN_BASE, "env.REACT_APP_DOMAIN_BASE");
    let code = marca ?? subdomains.includes(env.REACT_APP_DOMAIN_BASE) ? subdomain : window.location.hostname.split(".")[0];
    console.log(code, "company code");
    dispatch(setCompanyCode(code));
    // eslint-disable-next-line
  }, []);

  if (company.loading || loadingUser) return <Loading />;

  return (
    <div className={width > 765 ? "container" : ""} style={width > 765 ? { paddingRight: 200, paddingLeft: 200, width: "100vw" } : { width: "100vw" }}>
      <Routes>
        <Route path="/" element={<CompanyScreen />} />
        <Route path="/produto/:product_id" element={<ProductScreen />} />
        <Route path="/checkout" element={<CheckoutScreen />} />
        <Route path="/sacola" element={<OrderScreen />} />
        <Route path="/login" element={<LoginScreen />} />
        <Route path="/esqueci_minha_senha" element={<ForgotPasswordScreen />} />
        <Route path="/redefinir_senha" element={<ResetPasswordScreen />} />
        <Route path="/cadastro" element={<RegisterScreen />} />
        <Route path="/emepag_redirect" element={<EmepagRedirect />} />
        <Route path="/manutencao" element={<MaintenancePage />} />
        <Route path="/*" element={<NotFoundPage />} />
      </Routes>
      <ToastContainer />
      {company.data.id && <WhatsappButton />}
    </div>
  );
};

const App: React.FC = () => {
  return (
    <Routes>
      <Route path="logout" element={<LogoutScreen />} />
      <Route path="/*" element={<AppRoutes />} />
    </Routes>
  );
};

export default App;
